import { Button, Drawer, HStack, Icon, Portal, Spinner, Text } from "@chakra-ui/react";
import { IoCloseSharp } from "react-icons/io5";
import { useEffect, useState } from "react";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import CompanyOwnerFormFields from "./CompanyOwnerFormFields";
import { emptyCompanyOwnerForm, validateCompanyOwnerForm } from "../companyOwnerForm";

/**
 * Add Client = create the company and its first Owner in one form. Owner name + e-mail are the
 * only required fields; company name, billing, etc. are optional. With no company name the list
 * falls back to showing the owner's name.
 */
const CreateClientDrawer = ({ isOpen, onClose, onCreated }) => {
    const [form, setForm] = useState(emptyCompanyOwnerForm);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) setForm(emptyCompanyOwnerForm());
    }, [isOpen]);

    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    const handleCreate = async () => {
        const error = validateCompanyOwnerForm(form);
        if (error) {
            showToast("Error", error, "error");
            return;
        }
        setIsSaving(true);
        try {
            await ClientService.createCompany(form);
            showToast("Client created", "Activation email sent to the owner", "success");
            onCreated();
            onClose();
        } catch (err) {
            showToast("Error", err?.response?.data ?? "Failed to create client", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="lg" placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner padding="4">
                    <Drawer.Content bg="bg.drawer" borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent="space-between" w="full">
                                <Text color="white">Add Client</Text>
                                <Icon fontSize={32} color="text.grey" onClick={handleClose} cursor="pointer">
                                    <IoCloseSharp />
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <CompanyOwnerFormFields form={form} set={set} />
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent="flex-end" w="full" gap={3}>
                                <Button variant="outlined" onClick={handleClose} disabled={isSaving}>Cancel</Button>
                                <Button variant="primary" onClick={handleCreate} disabled={isSaving}>
                                    {isSaving ? <Spinner size="sm" /> : "Create"}
                                </Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};

export default CreateClientDrawer;
